<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import {
  assignPatient, createPatient, listPatients,
  type Measurement, type Patient,
} from '../api'

const props = defineProps<{ measurement: Measurement }>()
const emit = defineEmits<{
  (e: 'close'): void
  (e: 'assigned', m: Measurement): void
}>()

const patients = ref<Patient[]>([])
const loading = ref(true)
const search = ref('')
const error = ref<string | null>(null)
const busyKey = ref<string | null>(null)

const showNewForm = ref(false)
const newName = ref('')
const newDob = ref('')

const filtered = computed(() => {
  const q = search.value.trim().toLowerCase()
  return q
    ? patients.value.filter((p) => p.name.toLowerCase().includes(q))
    : patients.value
})

async function load() {
  loading.value = true
  error.value = null
  try {
    const all = await listPatients()
    patients.value = all.sort((a, b) => a.name.localeCompare(b.name, 'de'))
  } catch {
    error.value = 'Patientenliste konnte nicht geladen werden.'
  } finally {
    loading.value = false
  }
}

async function assignTo(p: Patient) {
  busyKey.value = p.id
  error.value = null
  try {
    const updated = await assignPatient(props.measurement.id, p.id)
    emit('assigned', updated)
    emit('close')
  } catch {
    error.value = 'Zuordnung fehlgeschlagen.'
    busyKey.value = null
  }
}

async function unassign() {
  busyKey.value = '__unassign'
  error.value = null
  try {
    const updated = await assignPatient(props.measurement.id, null)
    emit('assigned', updated)
    emit('close')
  } catch {
    error.value = 'Aufheben fehlgeschlagen.'
    busyKey.value = null
  }
}

async function createAndAssign() {
  if (!newName.value.trim()) {
    error.value = 'Name erforderlich.'
    return
  }
  busyKey.value = '__new'
  error.value = null
  try {
    const p = await createPatient({
      name: newName.value.trim(),
      date_of_birth: newDob.value || null,
    })
    const updated = await assignPatient(props.measurement.id, p.id)
    emit('assigned', updated)
    emit('close')
  } catch {
    error.value = 'Anlegen fehlgeschlagen.'
    busyKey.value = null
  }
}

function fmtDateTime(s: string): string {
  return new Date(s).toLocaleString('de-AT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function fmtDob(s: string | null): string {
  return s ? new Date(s).toLocaleDateString('de-AT') : ''
}

onMounted(load)
</script>

<template>
  <div class="backdrop" @click.self="emit('close')">
    <div class="modal">
      <div class="modal-header">
        <h2>Patient zuordnen</h2>
        <button class="close" @click="emit('close')" aria-label="Schließen">×</button>
      </div>
      <p class="small muted">Messung vom {{ fmtDateTime(measurement.timestamp) }}</p>

      <!-- Picker mode -->
      <template v-if="!showNewForm">
        <input
          v-model="search"
          type="search"
          placeholder="Patient suchen…"
          class="search"
        />

        <div v-if="loading" class="state muted">Lade…</div>
        <div v-else-if="error" class="state error">{{ error }}</div>
        <div v-else-if="!filtered.length" class="state muted">Keine Treffer.</div>
        <ul v-else class="patient-list">
          <li v-for="p in filtered" :key="p.id">
            <button
              class="row"
              :disabled="busyKey === p.id"
              @click="assignTo(p)"
            >
              <span class="name">{{ p.name }}</span>
              <span class="dob muted small" v-if="p.date_of_birth">
                * {{ fmtDob(p.date_of_birth) }}
              </span>
            </button>
          </li>
        </ul>

        <div class="actions">
          <button class="link" @click="showNewForm = true; error = null">
            + Neuen Patienten anlegen
          </button>
          <button
            v-if="measurement.patient_id"
            class="link danger"
            :disabled="busyKey === '__unassign'"
            @click="unassign"
          >
            Zuordnung aufheben
          </button>
        </div>
      </template>

      <!-- New patient mode -->
      <template v-else>
        <h3>Neuer Patient</h3>
        <label>
          <span>Name</span>
          <input v-model="newName" autofocus />
        </label>
        <label>
          <span>Geburtsdatum (optional)</span>
          <input v-model="newDob" type="date" />
        </label>
        <div v-if="error" class="state error">{{ error }}</div>
        <div class="form-actions">
          <button @click="showNewForm = false; error = null">Zurück</button>
          <button
            class="primary"
            :disabled="busyKey === '__new'"
            @click="createAndAssign"
          >
            Anlegen und zuordnen
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
.backdrop {
  position: fixed; inset: 0;
  background: rgba(5, 17, 19, 0.45);
  display: flex; align-items: center; justify-content: center;
  z-index: 50;
}
.modal {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  box-shadow: var(--shadow-md);
  width: 100%; max-width: 500px;
  padding: 24px 28px;
  max-height: 85vh; overflow-y: auto;
}
.modal-header { display: flex; justify-content: space-between; align-items: center; }
h2 {
  font-family: var(--font-display); font-weight: 500;
  font-size: 20px; letter-spacing: 0.01em;
}
h3 {
  font-family: var(--font-display); font-weight: 500;
  margin: 14px 0 8px; font-size: 16px;
}
.close {
  border: none; background: transparent; font-size: 24px; line-height: 1;
  color: var(--color-muted); cursor: pointer; padding: 0 6px;
}
.close:hover { color: var(--color-text); background: transparent; }

.small { font-size: 12px; }
.muted { color: var(--color-muted); margin: 4px 0 14px; }

.search { width: 100%; margin: 14px 0; }
.state { padding: 18px; text-align: center; }
.error { color: var(--color-danger); }

.patient-list {
  list-style: none; padding: 0; margin: 0;
  max-height: 340px; overflow-y: auto;
  border: 1px solid var(--color-border); border-radius: var(--radius);
}
.patient-list li { border-bottom: 1px solid var(--color-border); }
.patient-list li:last-child { border-bottom: none; }
.row {
  display: flex; justify-content: space-between; align-items: baseline;
  width: 100%; border: none; background: transparent;
  padding: 11px 16px; cursor: pointer; text-align: left;
  font: inherit; border-radius: 0;
}
.row:hover:not(:disabled) { background: var(--light-beige); }
.row:disabled { opacity: 0.5; }
.name { font-weight: 500; }
.dob {
  margin-left: 12px;
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
}

.actions { display: flex; justify-content: space-between; margin-top: 14px; }
button.link {
  border: none; background: transparent; color: var(--color-primary);
  padding: 6px 10px; cursor: pointer; font-size: 13px; font-weight: 500;
  border-radius: 6px;
}
button.link:hover:not(:disabled) {
  background: rgba(var(--salvia-rgb), 0.12);
  color: var(--salvia-dark);
}
button.link.danger { color: var(--color-danger); }
button.link.danger:hover:not(:disabled) { background: rgba(179, 74, 58, 0.08); }

label {
  display: flex; flex-direction: column; gap: 5px;
  margin: 12px 0; font-size: 13px;
}
label span { color: var(--color-muted); font-size: 12px; }
.form-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 18px; }
</style>
